import { useState, useEffect } from "react";
import { Link, useLocation, useParams, Outlet, useNavigate, Navigate } from "react-router-dom";
import {
  Search, ChevronDown, ChevronUp, LogOut, Menu, X, Bell, Globe, Maximize2, Moon, Zap, Lock,
  LayoutDashboard, ShoppingBag, UsersRound, UserCog, Package, FileEdit, ShoppingCart,
  Receipt, RotateCcw, Quote, Truck, AlertTriangle, BadgeDollarSign, Wallet, Briefcase,
  FolderKanban, CalendarRange, Settings, CreditCard, BarChart3, ArrowLeftRight,
  Store as StoreIcon, Banknote, Inbox, Brain, Workflow, UsersRound as UsersIcon, Building2, Plus, Trophy,
} from "lucide-react";
import { clearSession, getToken, getUser, getTenant } from "../../lib/auth";
import { CurrencyProvider } from "../../lib/currency";
import { toast } from "sonner";

// Map sidebar module keys to backend ALL_MODULES labels. Keys NOT in this map
// (or that resolve to null) are considered "always enabled" (core utilities).
const MODULE_BACKEND_LABEL = {
  dashboard: null, // always on
  store: "Store",
  people: "People",
  "user-management": "User Management",
  products: "Products",
  adjustment: "Adjustment",
  purchases: "Purchases",
  sales: "Sales",
  "sales-return": "Sales Return",
  quotations: "Quotations",
  "purchases-return": "Purchases Return",
  transfer: "Transfer",
  damages: "Damages",
  hrm: "HR Management",
  accounting: "Accounting",
  assets: "Assets",
  projects: "Projects",
  bookings: "Bookings",
  settings: null, // always on
  billing: null, // always on
  reports: "Reports",
  marketplace: "Marketplace",
  finance: "Finance",
  inbox: "Omnichannel Inbox",
  crm: "CRM",
  "ai-analytics": "AI Analytics",
  workflows: "Workflow Automation",
  "real-estate": "Real Estate",
  "football-club": "Football Club",
};

// 20 modules from user spec, with sub-items where appropriate
const MODULES = [
  { key: "dashboard", lbl: "Dashboard", icon: LayoutDashboard, to: "dashboard" },
  {
    key: "store", lbl: "Store", icon: ShoppingBag, children: [
      { lbl: "Visit Online Store", to: "store/visit" },
      { lbl: "Settings", to: "store/settings" },
      { lbl: "Online Orders", to: "store/orders" },
      { lbl: "Collections", to: "store/collections" },
      { lbl: "Banners", to: "store/banners" },
      { lbl: "Subscribers", to: "store/subscribers" },
      { lbl: "Messages", to: "store/messages" },
    ],
  },
  {
    key: "people", lbl: "People", icon: UsersRound, children: [
      { lbl: "Customers", to: "people/customers" },
      { lbl: "Create Customer", to: "people/customers/new" },
      { lbl: "Import Customers", to: "people/customers/import" },
      { lbl: "Suppliers", to: "suppliers" },
      { lbl: "Create Supplier", to: "suppliers/new" },
      { lbl: "Import Suppliers", to: "suppliers/import" },
    ],
  },
  {
    key: "user-management", lbl: "User Management", icon: UserCog, children: [
      { lbl: "Users", to: "user-management/users" },
      { lbl: "Group Permissions", to: "user-management/roles" },
    ],
  },
  {
    key: "products", lbl: "Products", icon: Package, children: [
      { lbl: "Create product", to: "products/new" },
      { lbl: "All Products", to: "products" },
      { lbl: "Import products", to: "products/import" },
      { lbl: "Import (Update Only)", to: "products/import-update" },
      { lbl: "Opening Stock", to: "products/opening-stock" },
      { lbl: "Print Labels", to: "products/print-labels" },
      { lbl: "Count Stock", to: "products/count-stock" },
      { lbl: "Stock by Branch", to: "stock-by-warehouse" },
      { lbl: "Branches", to: "products/warehouses" },
      { lbl: "Category", to: "products/categories" },
      { lbl: "Sub Category", to: "products/subcategories" },
      { lbl: "Brand", to: "products/brands" },
      { lbl: "Unit", to: "products/units" },
    ],
  },
  { key: "adjustment", lbl: "Adjustment", icon: FileEdit, children: [
      { lbl: "All Adjustments", to: "adjustment" },
      { lbl: "Create Adjustment", to: "adjustment/new" },
  ] },
  {
    key: "purchases", lbl: "Purchases", icon: ShoppingCart, children: [
      { lbl: "Create Purchase", to: "purchases/new" },
      { lbl: "All Purchases", to: "purchases" },
      { lbl: "Import Purchases", to: "purchases/import" },
    ],
  },
  {
    key: "sales", lbl: "Sales", icon: Receipt, children: [
      { lbl: "Create Sale", to: "sales/new" },
      { lbl: "Import Sales", to: "sales/import" },
      { lbl: "All Sales", to: "sales" },
      { lbl: "POS", to: "sales/pos" },
      { lbl: "Customer Screen", to: "sales/customer-screen" },
      { lbl: "Shipments", to: "sales/shipments" },
      { lbl: "Real-time Sales Counter", to: "sales/counter" },
    ],
  },
  {
    key: "sales-return", lbl: "Sales Return", icon: RotateCcw, children: [
      { lbl: "Create Sales Return", to: "sales-return/new" },
      { lbl: "All Sales Returns", to: "sales-return" },
    ],
  },
  { key: "quotations", lbl: "Quotations", icon: Quote, children: [
      { lbl: "All Quotations", to: "quotations" },
      { lbl: "Create Quotation", to: "quotations/new" },
  ] },
  {
    key: "purchases-return", lbl: "Purchases Return", icon: ArrowLeftRight, children: [
      { lbl: "Create Purchase Return", to: "purchases-return/new" },
      { lbl: "All Purchase Returns", to: "purchases-return" },
    ],
  },
  { key: "transfer", lbl: "Transfer", icon: Truck, children: [
      { lbl: "All Transfers", to: "transfer" },
      { lbl: "Create Transfer", to: "transfer/new" },
  ] },
  { key: "damages", lbl: "Damages", icon: AlertTriangle, children: [
      { lbl: "All Damages", to: "damages" },
      { lbl: "Log Damage", to: "damages/new" },
  ] },
  { key: "hrm", lbl: "HRM", icon: UsersRound, children: [
      { lbl: "Dashboard", to: "hrm" },
      { lbl: "Departments", to: "hrm/departments" },
      { lbl: "Designations", to: "hrm/designations" },
      { lbl: "Office Shift", to: "hrm/shifts" },
      { lbl: "Employees", to: "hrm/employees" },
      { lbl: "Attendance", to: "hrm/attendance" },
      { lbl: "Leave Request", to: "hrm/leaves" },
      { lbl: "Holidays", to: "hrm/holidays" },
      { lbl: "Payroll", to: "hrm/payrolls" },
  ] },
  { key: "accounting", lbl: "Accounting", icon: Wallet, children: [
      { lbl: "Dashboard", to: "accounting" },
      { lbl: "Chart of Accounts", to: "accounting/accounts" },
      { lbl: "Journal Entries", to: "accounting/journal-entries" },
      { lbl: "Trial Balance", to: "accounting/trial-balance" },
      { lbl: "Profit & Loss", to: "accounting/pl" },
      { lbl: "Balance Sheet", to: "accounting/balance-sheet" },
      { lbl: "Tax Summary", to: "accounting/tax-summary" },
      { lbl: "Expenses", to: "accounting/expenses" },
      { lbl: "Deposits", to: "accounting/deposits" },
      { lbl: "Expense Category", to: "accounting/expense-categories" },
      { lbl: "Deposit Category", to: "accounting/deposit-categories" },
      { lbl: "Transfers Money", to: "accounting/money-transfers" },
  ] },
  { key: "finance", lbl: "Finance", icon: Banknote, children: [
      { lbl: "Overview", to: "finance" },
      { lbl: "Chart of Accounts", to: "finance" },
      { lbl: "Bank Accounts", to: "finance" },
      { lbl: "Account Mappings", to: "finance" },
      { lbl: "Ledger History", to: "finance" },
  ] },
  { key: "inbox", lbl: "Inbox", icon: Inbox, children: [
      { lbl: "All Conversations", to: "inbox" },
      { lbl: "Channels", to: "inbox/channels" },
  ] },
  { key: "crm", lbl: "CRM", icon: UsersIcon, to: "crm" },
  { key: "ai-analytics", lbl: "AI Analytics", icon: Brain, to: "ai-analytics" },
  { key: "workflows", lbl: "Workflows", icon: Workflow, to: "workflows" },
  { key: "real-estate", lbl: "Real Estate", icon: Building2, to: "real-estate" },
  { key: "football-club", lbl: "Football Club", icon: Trophy, to: "football-club" },
  { key: "assets", lbl: "Assets", icon: Briefcase, to: "assets" },
  { key: "projects", lbl: "Projects", icon: FolderKanban, to: "projects" },
  { key: "bookings", lbl: "Bookings", icon: CalendarRange, to: "bookings" },
  { key: "marketplace", lbl: "Marketplace", icon: StoreIcon, children: [
      { lbl: "Browse Marketplace", to: "marketplace" },
      { lbl: "My Listings", to: "marketplace/listings" },
      { lbl: "Submit Product", to: "marketplace/submit" },
  ] },
  { key: "settings", lbl: "Settings", icon: Settings, to: "settings" },
  { key: "billing", lbl: "Billing", icon: CreditCard, to: "billing" },
  { key: "reports", lbl: "Reports", icon: BarChart3, children: [
      { lbl: "Hub", to: "reports" },
      { lbl: "Sales Report", to: "reports/sales" },
      { lbl: "Purchase Report", to: "reports/purchases" },
      { lbl: "Stock Report", to: "reports/stock" },
      { lbl: "Profit Report", to: "reports/profit" },
  ] },
  // Always render last — groups all un-purchased/locked modules for upsell
  { key: "add-modules", lbl: "Upgrade", icon: Plus, to: "add-modules" },
];

export default function TenantLayout() {
  const { subdomain } = useParams();
  const loc = useLocation();
  const nav = useNavigate();
  const user = getUser();
  const token = getToken();
  const tenant = getTenant();
  const [q, setQ] = useState("");
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(() => {
    if (loc.pathname.includes("/store/")) return "store";
    if (loc.pathname.includes("/products")) return "products";
    if (loc.pathname.includes("/sales-return")) return "sales-return";
    if (loc.pathname.includes("/purchases-return")) return "purchases-return";
    if (loc.pathname.includes("/sales")) return "sales";
    if (loc.pathname.includes("/purchases")) return "purchases";
    if (loc.pathname.includes("/people") || loc.pathname.includes("/suppliers")) return "people";
    if (loc.pathname.includes("/user-management")) return "user-management";
    if (loc.pathname.includes("/adjustment")) return "adjustment";
    if (loc.pathname.includes("/quotations")) return "quotations";
    if (loc.pathname.includes("/transfer")) return "transfer";
    if (loc.pathname.includes("/damages")) return "damages";
    if (loc.pathname.includes("/hrm")) return "hrm";
    if (loc.pathname.includes("/accounting")) return "accounting";
    if (loc.pathname.includes("/reports")) return "reports";
    if (loc.pathname.includes("/inbox")) return "inbox";
    if (loc.pathname.includes("/marketplace")) return "marketplace";
    if (loc.pathname.includes("/finance")) return "finance";
    return null;
  });

  // Auto-close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  if (!token || !user || !tenant || tenant.subdomain !== subdomain) {
    return <Navigate to={`/t/${subdomain}/login`} replace />;
  }

  const filteredModules = q
    ? MODULES.filter((m) =>
        m.lbl.toLowerCase().includes(q.toLowerCase()) ||
        m.children?.some((c) => c.lbl.toLowerCase().includes(q.toLowerCase()))
      )
    : MODULES;

  // Marketers see a slim, role-specific navigation (Marketer Portal only).
  const isMarketer = user?.role === "marketer";
  const isTeamLead = user?.role === "team_lead";
  // Permission-based filtering: tenant admins see everything; others see only modules whose perms they have.
  const userPerms = new Set(user?.permissions || []);
  const isAdmin = !!user?.is_tenant_admin;
  const MODULE_PERM_HINT = {
    dashboard: "dashboard.view",
    store: "store.view",
    people: "customers.manage",
    "user-management": "users.manage",
    products: "products.view",
    adjustment: "adjustment.view",
    purchases: "purchases.view",
    sales: "sales.view",
    "sales-return": "returns.view",
    quotations: "quotations.view",
    "purchases-return": "returns.view",
    transfer: "transfer.view",
    damages: "damages.view",
    hrm: "hrm.view",
    accounting: "accounting.view",
    finance: "finance.view",
    inbox: "inbox.view",
    crm: "crm.view",
    "ai-analytics": "ai_analytics.view",
    workflows: "workflows.manage",
    "real-estate": "real_estate.view",
    "football-club": "football_club.view",
    "add-modules": null,
    assets: "assets.manage",
    projects: "projects.manage",
    bookings: "bookings.manage",
    marketplace: "marketplace.manage",
    reports: "reports.view",
    settings: null,
    billing: null,
  };
  const hasAccessToModule = (key) => {
    if (isAdmin) return true;
    const perm = MODULE_PERM_HINT[key];
    if (!perm) return true;  // always on (settings/billing/dashboard)
    return userPerms.has(perm) || [...userPerms].some((p) => p.startsWith(perm.split(".")[0] + "."));
  };
  // Hide locked (un-activated) modules entirely — they live behind the "Upgrade" page now.
  // Core nav items always visible: dashboard, settings, billing, add-modules (Upgrade).
  const ALWAYS_VISIBLE = new Set(["dashboard", "settings", "billing", "add-modules"]);
  const enabledModulesSet = new Set(tenant.modules_enabled || []);
  const tenantHasEntitlements = (tenant.modules_enabled || []).length > 0;
  const isModuleActivated = (key) => {
    if (ALWAYS_VISIBLE.has(key)) return true;
    if (!tenantHasEntitlements) return true; // plans without explicit gating show all
    const label = MODULE_BACKEND_LABEL[key];
    if (label === null || label === undefined) return true; // not gated
    return enabledModulesSet.has(label);
  };
  const baseFilteredModules = filteredModules
    .filter((m) => hasAccessToModule(m.key))
    .filter((m) => isModuleActivated(m.key));
  const navModules = isMarketer
    ? [{ key: "marketer-portal", lbl: "My Marketer Portal", icon: Building2, to: "marketer-portal" }]
    : isTeamLead
      ? [{ key: "team-lead-portal", lbl: "Team Lead Portal", icon: Building2, to: "team-lead-portal" }]
      : baseFilteredModules;

  // Auto-redirect role-specific portals from /dashboard
  if (isMarketer && loc.pathname.endsWith("/dashboard")) {
    return <Navigate to={`/t/${subdomain}/marketer-portal`} replace />;
  }
  if (isTeamLead && loc.pathname.endsWith("/dashboard")) {
    return <Navigate to={`/t/${subdomain}/team-lead-portal`} replace />;
  }

  return (
    <CurrencyProvider>
      <TenantShell tenant={tenant} user={user} subdomain={subdomain} q={q} setQ={setQ}
        collapsed={collapsed} setCollapsed={setCollapsed}
        mobileOpen={mobileOpen} setMobileOpen={setMobileOpen}
        openGroup={openGroup} setOpenGroup={setOpenGroup}
        filteredModules={navModules} loc={loc} nav={nav} />
    </CurrencyProvider>
  );
}

function TenantShell({ tenant, user, subdomain, q, setQ, collapsed, setCollapsed, mobileOpen, setMobileOpen, openGroup, setOpenGroup, filteredModules, loc, nav }) {
  const logout = () => { clearSession(); nav(`/t/${subdomain}/login`); };
  const base = `/t/${subdomain}`;

  // Module entitlement: if tenant has modules_enabled populated, modules not in it are locked.
  // Core modules (dashboard/settings/billing) are always on.
  const enabledSet = new Set(tenant.modules_enabled || []);
  const hasEntitlements = (tenant.modules_enabled || []).length > 0;
  const isModuleLocked = (key) => {
    const label = MODULE_BACKEND_LABEL[key];
    if (label === null || label === undefined) return false;
    if (!hasEntitlements) return false;
    return !enabledSet.has(label);
  };
  const onLockClick = () => nav(`${base}/add-modules`);

  const isActive = (to) => {
    if (!to) return false;
    const full = `${base}/${to}`;
    return loc.pathname === full || loc.pathname.startsWith(full + "/");
  };
  // On mobile (when drawer is open), force expanded view — ignore desktop `collapsed`
  const expanded = mobileOpen || !collapsed;

  return (
    <div data-testid="tenant-layout" className="min-h-screen bg-[#F4F5FA] lg:flex">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <button
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-nav-backdrop"
          className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
        />
      )}

      {/* SIDEBAR */}
      <aside
        data-testid="tenant-sidebar"
        className={`${collapsed ? "lg:w-20" : "lg:w-64"} w-72 bg-white border-r border-black/8 flex flex-col lg:sticky fixed top-0 left-0 h-screen z-50 transition-transform lg:transition-all
                    ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        {/* Logo */}
        <div className="h-16 px-4 border-b border-black/8 flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#1B4332] to-[#52B788] flex items-center justify-center flex-shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M4 6h12a4 4 0 010 8H4V6z" stroke="white" strokeWidth="2.5" strokeLinejoin="round"/>
              <path d="M4 14h14a4 4 0 010 8H4v-8z" stroke="white" strokeWidth="2.5" strokeLinejoin="round"/>
            </svg>
          </div>
          {expanded && (
            <div className="font-display font-extrabold text-xl text-black truncate flex-1">{tenant.company_name}</div>
          )}
          <button onClick={() => setMobileOpen(false)} data-testid="mobile-nav-close"
            className="lg:hidden p-2 rounded-lg hover:bg-black/5 text-black/60">
            <X size={18} />
          </button>
        </div>

        {/* Search */}
        {expanded && (
          <div className="p-3 border-b border-black/8">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="tenant-sidebar-search"
                placeholder="Search…"
                className="w-full pl-9 pr-3 py-2 rounded-lg bg-[#F4F5FA] border border-transparent focus:bg-white focus:border-[#1FA868] focus:outline-none text-sm" />
            </div>
          </div>
        )}

        {/* Modules */}
        <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto">
          {filteredModules.map((m) => {
            const Icon = m.icon;
            const isExp = openGroup === m.key;
            const hasChildren = !!m.children?.length;
            const locked = isModuleLocked(m.key);

            // LOCKED by plan — quiet lock icon, click to Add Modules page (Q5=b)
            if (locked) {
              return (
                <button key={m.key} onClick={() => nav(`${base}/add-modules`)}
                  data-testid={`nav-${m.key}`}
                  title="Locked — click to add this module"
                  className="w-full group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-black/40 hover:bg-black/[0.04] hover:text-black/65">
                  <Icon size={17} className="text-black/30 group-hover:text-black/55" />
                  {expanded && (
                    <>
                      <span className="flex-1 truncate text-left">{m.lbl}</span>
                      <Lock size={11} className="text-black/30" />
                    </>
                  )}
                </button>
              );
            }

            // Direct link (Dashboard)
            if (!hasChildren && m.to) {
              const active = isActive(m.to);
              return (
                <Link key={m.key} to={`${base}/${m.to}`} data-testid={`nav-${m.key}`}
                  className={`group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                    active
                      ? "bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white shadow-sm"
                      : "text-black/70 hover:bg-[#F4F5FA] hover:text-black"
                  }`}>
                  <Icon size={17} className={active ? "text-white" : "text-black/45 group-hover:text-[#1FA868]"} />
                  {expanded && (
                    <>
                      <span className="flex-1 truncate">{m.lbl}</span>
                      {m.soon && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700">Soon</span>}
                    </>
                  )}
                </Link>
              );
            }

            // Soon (no children, no route)
            if (!hasChildren && m.soon) {
              return (
                <button key={m.key} disabled data-testid={`nav-${m.key}`}
                  className="w-full group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-black/45 hover:bg-[#F4F5FA] cursor-not-allowed">
                  <Icon size={17} className="text-black/35" />
                  {expanded && (
                    <>
                      <span className="flex-1 truncate text-left">{m.lbl}</span>
                      <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700">Soon</span>
                    </>
                  )}
                </button>
              );
            }

            // Group with children (Store, User Management)
            const childActive = m.children.some((c) => isActive(c.to));
            return (
              <div key={m.key}>
                <button onClick={() => setOpenGroup(isExp ? null : m.key)} data-testid={`nav-${m.key}`}
                  className={`w-full group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                    childActive && !isExp
                      ? "bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white"
                      : "text-black/75 hover:bg-[#F4F5FA] hover:text-black"
                  }`}>
                  <Icon size={17} className={childActive && !isExp ? "text-white" : "text-black/45 group-hover:text-[#1FA868]"} />
                  {expanded && (
                    <>
                      <span className="flex-1 truncate text-left">{m.lbl}</span>
                      {isExp ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </>
                  )}
                </button>
                {expanded && isExp && (
                  <div className="ml-3 mt-1 mb-1 pl-3 border-l border-black/10 space-y-0.5">
                    {m.children.map((c) => {
                      const active = isActive(c.to);
                      return c.soon ? (
                        <button key={c.lbl} disabled data-testid={`nav-${c.lbl.toLowerCase().replace(/\s+/g, '-')}`}
                          className="w-full text-left px-3 py-2 rounded-lg text-xs font-medium text-black/40 cursor-not-allowed flex items-center justify-between">
                          {c.lbl}
                          <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Soon</span>
                        </button>
                      ) : (
                        <Link key={c.lbl} to={`${base}/${c.to}`} data-testid={`nav-${c.lbl.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`block px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                            active ? "bg-[#E8F5E9] text-[#1FA868]" : "text-black/65 hover:bg-[#F4F5FA] hover:text-black"
                          }`}>
                          {c.lbl}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* MAIN COLUMN */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="h-16 bg-white border-b border-black/8 px-3 sm:px-5 flex items-center gap-2 sm:gap-3 sticky top-0 z-30">
          {/* Mobile hamburger (hidden on lg+) */}
          <button onClick={() => setMobileOpen(true)} data-testid="mobile-nav-toggle"
            className="lg:hidden p-2 rounded-lg hover:bg-black/5 text-black/70">
            <Menu size={20} />
          </button>
          {/* Desktop sidebar collapse (hidden on mobile) */}
          <button onClick={() => setCollapsed(expanded)} data-testid="sidebar-toggle"
            className="hidden lg:inline-flex p-2 rounded-lg hover:bg-black/5">
            {collapsed ? <Menu size={18} /> : <X size={18} />}
          </button>
          {/* Mobile brand */}
          <Link to={base} className="lg:hidden flex items-center gap-2 min-w-0 flex-1">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1B4332] to-[#52B788] flex items-center justify-center flex-shrink-0">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M4 6h12a4 4 0 010 8H4V6z" stroke="white" strokeWidth="2.5" strokeLinejoin="round"/>
                <path d="M4 14h14a4 4 0 010 8H4v-8z" stroke="white" strokeWidth="2.5" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="font-display font-extrabold text-sm text-black truncate">{tenant.company_name}</span>
          </Link>
          <div className="hidden lg:block flex-1" />
          <Link to={`/store/${subdomain}`} target="_blank" data-testid="topbar-pos"
            className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white text-xs font-bold shadow-sm hover:opacity-95">
            <Zap size={12} /> POS
          </Link>
          <button data-testid="topbar-fullscreen" className="hidden sm:inline-flex p-2 rounded-lg hover:bg-black/5 border border-black/8">
            <Maximize2 size={15} className="text-black/60" />
          </button>
          <button data-testid="topbar-language" className="hidden sm:inline-flex p-2 rounded-lg hover:bg-black/5 border border-black/8">
            <Globe size={15} className="text-black/60" />
          </button>
          <button data-testid="topbar-notifications" className="relative p-2 rounded-lg hover:bg-black/5 border border-black/8">
            <Bell size={15} className="text-black/60" />
            <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-rose-500" />
          </button>
          <div className="hidden sm:block w-px h-6 bg-black/10" />
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1FA868] to-[#16834D] text-white flex items-center justify-center font-bold text-sm">
              {(user?.name || "U")[0]}
            </div>
            <div className="hidden md:block">
              <div className="text-sm font-semibold text-black leading-none">{user?.name?.split(" ")[0] || "User"}</div>
              <div className="text-[10px] text-black/55 mt-0.5">{user?.role?.replace("_", " ")}</div>
            </div>
            <button onClick={logout} data-testid="tenant-logout"
              className="p-2 rounded-lg hover:bg-rose-50 hover:text-rose-600 text-black/55 ml-1">
              <LogOut size={15} />
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
